/**
 * ThemeToggle — анимированный переключатель дневной/ночной темы (солние↔луна).
 *
 * Виджет изолирован от темы приложения: сам только меняет атрибут `data-theme`
 * на корневом [`<html>`] и сохраняет выбор в `localStorage`. Применение темы
 * (переопределение CSS-переменных) — в [`index.css`](../index.css) под селектором
 * `[data-theme="light"]` (базовые значения `:root` — тёмная тема, т.к. приложение
 * исторически тёмное). Применение **до первого рендера** делает инлайн-скрипт
 * в [`index.html`](../../index.html) (читает `localStorage`/`prefers-color-scheme`
 * и ставит `data-theme` в `<head>`) — это убирает «вспышку» неправильной темы.
 *
 * Семантика чекбокса: `checked === dark` (луна = ночь), как в оригинале виджета.
 *
 * Все CSS-классы namespaced префиксом `theme-switch`/`ts-`, чтобы не конфликтовать
 * с `.container`/`.bg`/`.sun`/`.star`/`.cloud`/`.moon`/`.crater` приложения.
 * `transition` scope'нут только на потомков виджета (см. `.theme-switch *`) —
 * глобальный `* { transition: all }` намеренно НЕ используется (он ломает
 * анимации/производительность приложения).
 */
import { useEffect, useState } from 'react';
// apexDomainFromHost нужен, чтобы ставить cookie с Domain=.<apex> — общий для всех
// языковых поддоменов (kikayoo.com / en. / cn.). Ср. с LanguageSwitcher.url.ts.
import { apexDomainFromHost } from './LanguageSwitcher.url';

/**
 * Ключ, под которым выбор темы сохраняется в localStorage **и** в cookie.
 * В cookie значение шарится между языковыми поддоменами через Domain=.<apex>
 * (см. writeThemeCookie); localStorage служит быстрым локальным кэшем.
 */
export const THEME_KEY = 'kikayoo-theme';

export type Theme = 'light' | 'dark';

/** Читает текущую тему из атрибута data-theme корневого элемента (источник истины в DOM). */
function currentTheme(): Theme {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

/**
 * Чистая (без window/document) функция: строит строку cookie выбора темы по
 * заданному hostname. Вынесена отдельно, чтобы покрыть логику `Domain=.<apex>`
 * table-driven тестом без моков окружения (ср. с подходом в LanguageSwitcher.url.ts:
 * jsdom всё равно фильтрует document.cookie по своему внутреннему URL, поэтому
 * подменой window.location ветку Domain не проверить).
 *
 * Поведение:
 *  - на домене **с точкой** (apex) → `Domain=.<apex>`: cookie автоматически
 *    шарится между всеми языковыми поддоменами (kikayoo.com / `en.` / `cn.`).
 *    Это и делает тему общей при смене поддомена.
 *  - на **bare-хосте** без точки (`localhost`, `127.0.0.1`) → без `Domain`:
 *    браузеры режут `Domain=.localhost`, а нам важнее не «молча» потерять cookie
 *    в dev. Шаринг в dev не критичен, на проде hostname всегда с точкой.
 */
export function buildThemeCookie(theme: Theme, hostname: string): string {
  const apex = apexDomainFromHost(hostname);
  // Domain=.<apex> имеет смысл только для **настоящего домена** — есть и точка,
  // и буквы. Тогда cookie шарится между поддоменами. localhost (без точки) и
  // IP-адреса вида 127.0.0.1 (без букв) под критерий не подходят: браузеры
  // режут/игнорируют `Domain=.localhost` и `Domain=.127.0.0.1`, поэтому там
  // cookie ставится без Domain и живёт только на текущем хосте dev-сервера.
  const looksLikeDomain = apex.includes('.') && /[a-z]/i.test(apex);
  const withDomain = looksLikeDomain ? `; Domain=.${apex}` : '';
  return `${THEME_KEY}=${theme}; Path=/${withDomain}; Max-Age=31536000; SameSite=Lax`;
}

/**
 * Ставит cookie темы на текущем хосте (см. buildThemeCookie). Источник истины
 * при шаринге между поддоменами; парное **чтение** — в инлайн-скрипте index.html
 * (cookie доступен синхронно до выполнения JS, localStorage нового поддомена — нет).
 */
function writeThemeCookie(theme: Theme): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  document.cookie = buildThemeCookie(theme, window.location.hostname);
}

/**
 * Применяет тему: ставит `data-theme` на `<html>` (включает нужный набор
 * CSS-переменных), сохраняет выбор в localStorage (мгновенный локальный кэш)
 * **и** в cookie на родительском домене (источник истины при шаринге между
 * поддоменами), а также синхронизирует `<meta name="theme-color">` под
 * мобильные браузеры.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  try {
    // localStorage — быстрый локальный кэш (без парсинга document.cookie).
    localStorage.setItem(THEME_KEY, theme);
    // cookie Domain=.<apex> — общий выбор для всех языковых поддоменов.
    writeThemeCookie(theme);
  } catch {
    /* localStorage/cookie недоступны (приватный режим / квота) — tema всё равно
     * применяется на текущую сессию через data-theme, просто не сохранится. */
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#0f172a' : '#eef2f7');
}

/**
 * Skeuomorphic-тоггл солние↔луна в правом верхнем углу (position: fixed, см. CSS).
 * Чекбокс sr-only (фокусируемый с клавиатуры); клик/тап по всей капсуле — через
 * оборачивающий `<label>`. Анимация смены суток — на чистом CSS через
 * `:checked ~ .ts-container`.
 */
export function ThemeToggle(): JSX.Element {
  // checked отражает тёмную тему (луна). На первом рендере ориентируемся на
  // data-theme, уже выставленный инлайн-скриптом в <head> (без вспышки).
  const [checked, setChecked] = useState<boolean>(() => currentTheme() === 'dark');

  // Подстраховка: если data-theme поменяли извне (расширение/другой код) —
  // синхронизируем внутренний state при монтировании.
  useEffect(() => {
    setChecked(currentTheme() === 'dark');
  }, []);

  function onChange(): void {
    const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setChecked(next === 'dark');
  }

  return (
    <span className="theme-switch" data-testid="theme-toggle">
      <label className="theme-switch__label" title={checked ? 'Светлая тема' : 'Тёмная тема'}>
        <input
          className="theme-switch__input"
          type="checkbox"
          checked={checked}
          onChange={onChange}
          aria-label={checked ? 'Включить светлую тему' : 'Включить тёмную тему'}
          data-testid="theme-toggle-input"
        />
        <span className="ts-container">
          <span className="ts-bg" />
          <span className="ts-ray ts-ray-inner" />
          <span className="ts-ray ts-ray-far" />
          <span className="ts-cloud ts-cloud-1" />
          <span className="ts-cloud ts-cloud-2" />
          <span className="ts-cloud ts-cloud-3" />
          <span className="ts-star ts-star-1" />
          <span className="ts-star ts-star-2" />
          <span className="ts-star ts-star-3" />
          <span className="ts-star ts-star-4" />
          <span className="ts-star ts-star-5" />
          <span className="ts-sun">
            <span className="ts-moon">
              <span className="ts-crater ts-crater-1" />
              <span className="ts-crater ts-crater-2" />
              <span className="ts-crater ts-crater-3" />
            </span>
          </span>
        </span>
      </label>
    </span>
  );
}
